import { useCountry } from '@lt/localization';
import classNames from 'classnames';
import React, { lazy, Suspense, useEffect } from 'react';
import styled from 'styled-components';
import { scrollToElement } from 'src/utils/domUtils';
import { dynamic } from 'src/helpers/dynamic';
import SubtitleContainer from '../../SubtitleContainer';
import LazySubscribe from '../../../containers/Subscribe/LazySubscribe';
import LazyFilters from '../../Filters/components/FiltersPanel';
import Hotels from '../../Hotels';
import LazyPriceChart from '../../PriceChart/LazyPriceChart';
import Title from '../../Title';

const EmptySearch = lazy(
  () => import(/* webpackChunkName: "EmptySearch" */ '../../EmptySearch'),
);
const EmptyFiltration = lazy(
  () =>
    import(/* webpackChunkName: "EmptyFiltration" */ '../../EmptyFiltration'),
);

const YandexMapsSearch = lazy(
  () =>
    import(/* webpackChunkName: "YandexMapsSearch" */ '../../YandexMapsSearch'),
);

const NoTopHotel = dynamic(
  () => import(/* webpackChunkName: "NoTopHotel" */ '../../NoTopHotel'),
);

type SearchPageMobileProps = {
  hasSearchResults?: boolean;
  mapView: boolean;
  hideFilters: boolean;
  isSubscribeShown: boolean;
  isEmptySearch: boolean;
  isEmptyFiltration: boolean;
  isHotelOnly: boolean;
  showResults: boolean;
  showNoTopHotel: boolean;
};

const SearchPageMobile = ({
  hideFilters,
  mapView,
  hasSearchResults = false,
  isSubscribeShown,
  isEmptySearch,
  isEmptyFiltration,
  isHotelOnly,
  showResults,
  showNoTopHotel,
}: SearchPageMobileProps): JSX.Element => {
  useEffect(() => {
    scrollToElement(document.body);
  }, [hideFilters]);

  useEffect(() => {
    // Когда открываем/закрываем карту - скроллим вверх страницы
    window.scrollTo(0, 0);
  }, [mapView]);

  const showSorting = showResults && hasSearchResults && !showNoTopHotel;

  const isEmptyResult = isEmptySearch || isEmptyFiltration;
  const { isRussia } = useCountry();

  const contentClasses = classNames({
    'search-page__center': true,
    'search-page__center--mobile-map': mapView,
  });

  const showTitle = !showNoTopHotel && showResults;

  const showPriceChart =
    !mapView &&
    hideFilters &&
    !isSubscribeShown &&
    !isHotelOnly &&
    isRussia &&
    !isEmptyResult &&
    !showNoTopHotel;

  return (
    <div className="search-page-mobile">
      {showTitle && <StyledTitle />}
      {showPriceChart && <PriceChart />}
      {!hideFilters && (
        <LazyFilters show={!hideFilters} isHotelOnly={isHotelOnly} isMobile />
      )}
      {isSubscribeShown && !isHotelOnly && isRussia ? <LazySubscribe /> : null}
      {showSorting ? <SubtitleContainer /> : null}

      <div className="search-page__content mobile">
        <div className={contentClasses}>
          {mapView ? (
            <Suspense fallback={null}>
              <YandexMapsSearch />
            </Suspense>
          ) : null}
          {showResults ? (
            <>
              {showNoTopHotel ? <NoTopHotel /> : null}
              <Hotels />
            </>
          ) : null}

          <Suspense fallback={null}>
            {isEmptySearch && <EmptySearch />}
            {isEmptyFiltration && <EmptyFiltration />}
          </Suspense>
        </div>
      </div>
    </div>
  );
};

const StyledTitle = styled(Title)`
  margin: 0 10px 5px;
  padding-top: 10px;
`;

const PriceChart = styled(LazyPriceChart)`
  margin-bottom: 16px;
`;

export default SearchPageMobile;
